import { useEffect, useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { useIsUserSignedIn } from "../../../core_stores/useAuthStore";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import {
  useSubscriptionActions,
  useUserSubscription,
} from "../../../core_stores/useSubscriptionStore";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import { formatDateFromDDMMYYYY } from "../../../utils/dateFormatter";
import { formatTokensNumber } from "../../../utils/numberFormatter";
import { useArePersonalSettingsOpen } from "../../../view_stores/useSideMenuStore";
import { typography, components } from "../../../design/tokens";

export default function PersonalSettingsPlans() {
  return (
    <div className="text-dark-text flex flex-col">
      <PlanOverview />
      <hr className="border-dark-border my-5" />
      <div className="flex flex-col">
        <div className="w-full">
          <TokensUsage />
        </div>
        <hr className="border-dark-border my-5" />
        <div className="w-full">
          <BillingPeriod />
        </div>
      </div>
      <hr className="border-dark-border my-5" />
      <RedirectToUserPanel />
    </div>
  );
}

function PlanOverview() {
  const isCompactView = useIsCompactView();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();
  const subscription = useUserSubscription();

  return (
    <div
      className={`${
        isCompactView && "flex-col gap-4"
      } flex items-center justify-between`}
    >
      <div className="flex flex-col gap-2">
        <div className={`flex gap-2 items-center ${typography.size.base}`}>
          <span className={typography.weight.semibold}>Your Plan</span>
          <span className={`${typography.weight.semibold} text-blue-400`}>
            {subscription?.plan ?? ""}
          </span>
        </div>
      </div>
      <button
        id="personal-settings-plans-upgrade-plan"
        onClick={openSubscriptionsModal}
        className={`${isCompactView && "w-full"} ${components.button.primary}`}
      >
        Upgrade Plan
      </button>
    </div>
  );
}

function TokensUsage() {
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();
  const isUserSignedIn = useIsUserSignedIn();
  const subscription = useUserSubscription();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  useEffect(() => {
    if (isUserSignedIn && arePersonalSettingsOpen) {
      reloadSubscriptionsInfo();
    }
  }, [isUserSignedIn, arePersonalSettingsOpen]);

  const tokensLimit = subscription ? Number(subscription.limit) : undefined;
  const tokensUsed = subscription ? subscription.used : undefined;
  const formattedTokensLimit =
    tokensLimit === undefined ? "N/A" : formatTokensNumber(tokensLimit);
  const formattedTokensRemaining =
    subscription === null ? "N/A" : formatTokensNumber(subscription.remaining);
  const formattedTokensUsed =
    tokensUsed === undefined ? "N/A" : formatTokensNumber(tokensUsed);

  return (
    <div className="flex flex-col gap-3">
      {/* Primary: Tokens remaining - large and prominent */}
      <div className="flex flex-col gap-1">
        <span className={`${typography.size.sm} ${typography.weight.light}`}>
          Tokens remaining
        </span>
        <span className={`${typography.size["2xl"]} ${typography.weight.bold}`}>
          {formattedTokensRemaining}
        </span>
      </div>

      <hr className="border-dark-border" />

      {/* Secondary: This month's usage */}
      <div
        className={`flex items-center justify-between ${typography.size.sm}`}
      >
        <span className={typography.weight.light}>This month</span>
        <span>
          Used: {formattedTokensUsed} of {formattedTokensLimit}
        </span>
      </div>
    </div>
  );
}

function BillingPeriod() {
  const subscription = useUserSubscription();

  return (
    <div className="w-full flex flex-col gap-3">
      <div className={`${typography.size.base} ${typography.weight.semibold}`}>Billing Period</div>
      <div className={`flex flex-col gap-1 ${typography.size.sm} ${typography.weight.light}`}>
        <div className="flex items-center justify-between">
          <div>Start</div>
          <div>
            {subscription?.period.start
              ? formatDateFromDDMMYYYY(subscription.period.start)
              : "N/A"}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>End</div>
          <div>
            {subscription?.period.end
              ? formatDateFromDDMMYYYY(subscription.period.end)
              : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
}

function RedirectToUserPanel() {
  const { navigateToUserPanel } = useSubscriptionActions();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const onClick = async () => {
    setIsRedirecting(true);
    await navigateToUserPanel();
    setIsRedirecting(false);
  };

  return (
    <button
      id="personal-settings-plans-redirect-to-user-panel"
      onClick={onClick}
      className={`${typography.size.sm} text-blue-400 bg-transparent
        cursor-pointer flex justify-center items-center gap-1
        transition-all duration-300
        border border-transparent hover:border-white
        px-2 py-1 rounded`}
      disabled={isRedirecting}
    >
      Manage your payment preferences and check past payments
      {isRedirecting && <LuLoaderCircle className="animate-spin ml-1" />}
    </button>
  );
}
